"use client";

import React from "react";
import { Sun, Activity, Battery, Zap, Cpu, ShieldCheck, BookOpen } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const modulesData = [{
  id: "mod-1",
  title: "Fundamentos de Energía Fotovoltaica",
  description: "Estudio exhaustivo de la radiación solar, el efecto fotovoltaico y las especificaciones técnicas de las células solares de silicio monocristalino y policristalino.",
  duration: "4 Semanas",
  code: "SOL-101",
  icon: "sun"
}, {
  id: "mod-2",
  title: "Componentes y Protecciones",
  description: "Análisis de estructuras de montaje, conductores específicos para intemperie, conectores MC4 y dispositivos de protección para corriente continua (CC).",
  duration: "4 Semanas",
  code: "SOL-102",
  icon: "cpu"
}, {
  id: "mod-3",
  title: "Inversores y Electrónica de Potencia",
  description: "Topologías de inversores de cadena, microinversores, optimizadores de potencia y configuración avanzada de controladores de carga MPPT.",
  duration: "3 Semanas",
  code: "SOL-201",
  icon: "zap"
}, {
  id: "mod-4",
  title: "Almacenamiento de Energía",
  description: "Tecnologías de baterías de litio y ciclo profundo, cálculo de profundidad de descarga, autonomía y gestión de sistemas de almacenamiento (BMS).",
  duration: "5 Semanas",
  code: "SOL-202",
  icon: "battery"
}, {
  id: "mod-5",
  title: "Diseño y Dimensionamiento",
  description: "Cálculo de consumos, análisis de sombras con herramientas profesionales, dimensionamiento de arreglos fotovoltaicos y diseño de sistemas aislados e híbridos.",
  duration: "4 Semanas",
  code: "SOL-301",
  icon: "activity"
}, {
  id: "mod-6",
  title: "Instalación y Normativa",
  description: "Protocolos de seguridad en altura, puestas a tierra, mantenimiento preventivo, termografía de paneles y cumplimiento de reglamentaciones eléctricas vigentes.",
  duration: "3 Semanas",
  code: "SOL-302",
  icon: "shield"
}];
const iconMap: Record<string, React.ElementType> = {
  sun: Sun,
  activity: Activity,
  battery: Battery,
  zap: Zap,
  cpu: Cpu,
  shield: ShieldCheck
};
function ModuleCard({
  module,
  "data-index": dataIndex
}: {
  module: typeof modulesData[0];
  "data-index": number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = iconMap[module.icon] || BookOpen;
  return <div ref={ref} data-index={dataIndex} className={cn("relative w-full rounded-[24px] bg-sidebar-foreground/70 backdrop-blur-[12px] border border-white/10 p-6 md:p-8", "flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-center", "transition-all duration-700 ease-out", "hover:scale-[1.02] hover:shadow-[0_0_30px_var(--color-primary)] hover:border-primary/30 hover:z-10", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")} style={{
    transitionDelay: `${dataIndex * 100}ms`
  }}>
      <div className="shrink-0 p-4 rounded-2xl bg-white/5 border border-white/10 transition-transform duration-500 group-hover:scale-110">
        <Icon className="w-8 h-8 text-secondary drop-shadow-[0_0_12px_var(--color-secondary)]" strokeWidth={1.5} />
      </div>

      <div className="flex-1">
        <h3 className="font-sans font-bold text-xl md:text-2xl text-primary-foreground mb-3 tracking-tight">
          {module.title}
        </h3>
        <p className="font-sans text-primary-foreground/80 leading-relaxed text-sm md:text-base">
          {module.description}
        </p>
      </div>

      <div className="shrink-0 flex flex-row md:flex-col gap-4 md:gap-2 items-center md:items-end w-full md:w-auto pt-5 md:pt-0 border-t border-white/10 md:border-t-0 mt-2 md:mt-0">
        <div className="font-mono text-sm font-semibold text-primary-foreground bg-white/10 px-3 py-1.5 rounded-full tracking-wider">
          {module.code}
        </div>
        <div className="font-mono text-sm text-primary-foreground/60 tracking-wide">
          {module.duration}
        </div>
      </div>
    </div>;
}
export default function ModulosDelCurso() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="modulos-del-curso" className="relative bg-background py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-5xl">
        <div ref={headerRef} className={cn("text-center mb-16 md:mb-24 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-sans font-bold text-3xl md:text-5xl text-foreground mb-6 tracking-tight">
            Programa Académico
          </h2>
          <p className="font-sans text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto">
            Un plan de estudios estructurado con rigor técnico, diseñado para formar especialistas de alto nivel en el sector energético.
          </p>
        </div>

        <div className="flex flex-col gap-6 md:gap-8">
          {modulesData.map((module, index) => <ModuleCard key={module.id} module={module} data-index={index} />)}
        </div>
      </div>
    </section>;
}